<script setup>
import PrematchSportsNavigation from '@/components/PrematchSportsNavigation.vue';
import MainEvents from './Partials/MainEvents.vue';
import MainLeagues from './Partials/MainLeagues.vue';
import Carousel from '@/components/App/Carousel.vue';
import MainEventCard from './Partials/MainEventCard.vue';
import Spinner from '@/components/Spinner.vue';
import { storeToRefs } from 'pinia';
import { onMounted, ref, watch } from 'vue';
import { useFixturesStore } from '@/stores/fixtures';
import { useVisibleItems } from '@/composables/useVisibleItems';

const fixtures = useFixturesStore()
const opening = ref(false)
const { upcomingEvents , tenEvents } = storeToRefs(fixtures)
const { getMainFixtures } = storeToRefs(fixtures)
const liveSelectionsMap = ref({})
const preSelectionsMap = ref({})
const liveScoreboardsMap = ref({})

const loadTen = async (ids) => {
  if (!ids.length) return
  const res = await fixtures.fetchSelections(ids, "live", [20889])
  for (const ev of res) {
    liveSelectionsMap.value[ev.fi] = ev.m || []
  }
  const sbs = await fixtures.fetchScoreboards(ids)
  if (Array.isArray(sbs)) {
    sbs.forEach(sb => { liveScoreboardsMap.value[sb.fi] = sb })
  }
}

const loadUpcoming = async (ids) => {
  if (!ids.length) return
  const res = await fixtures.fetchSelections(ids, "pre", [20890])
  for (const ev of res) {
    preSelectionsMap.value[ev.fi] = ev.m || []
  }
}

const tenObserver = useVisibleItems(loadTen, { selector: '.ten-event-item' })
const upcomingObserver = useVisibleItems(loadUpcoming, { selector: '.upcoming-event-item' })

onMounted(() => {
  fixtures.fetchMainCompetitions()
  fixtures.fetchMainFixtures()
  tenObserver.observe()
  upcomingObserver.observe()
})

// Re-observe as the event lists change (e.g. live fixtures streaming in),
// so newly rendered rows also get their odds loaded.
watch(() => tenEvents.value(1).map(e => e.i).join(','), () => tenObserver.observe())
watch(() => upcomingEvents.value(1).map(e => e.i).join(','), () => upcomingObserver.observe())
</script>

<template>
  <div class="xl:flex flex-1 overflow-hidden">
    <div class="flex-1 w-full">
      <PrematchSportsNavigation class="py-2.5"/>
      <div class="pt-1.5 px-3" v-if="getMainFixtures(1).length">
        <Carousel
          height="250px"
          :items="getMainFixtures(1)"
          :slidesPerView="1"
          :spaceBetween="20"
          :showNavigation="true"
          :showPagination="true"
          :autoplay="false"
          :swiperOptions="{ loop: true }"
        >
          <template #slide="{ slide }">
            <MainEventCard :event="slide" />
          </template>
        </Carousel>
      </div>
      <MainLeagues />
      <MainEvents
        :title="$t('partials.17')"
        :sport="1"
        :market="20889"
        :events="tenEvents(1)"
        :inline="false"
        class="mt-4 px-3 py-1.5"
        :selectionsMap="liveSelectionsMap"
        :scoreboardsMap="liveScoreboardsMap"
        :live="true"
        item-class="ten-event-item"
      />
      <MainEvents
        :title="$t('partials.18')"
        :sport="1"
        :market="20890"
        :events="upcomingEvents(1)"
        :inline="false"
        class="mt-4 px-3 py-1.5"
        :selectionsMap="preSelectionsMap"
        :live="false"
        item-class="upcoming-event-item"
      />
    </div>
    <Spinner :show="opening" />
  </div>
</template>

<style scoped>
.show-btn {
    @apply text-center flex items-center justify-center gap-1 text-[var(--color-primary4)] hover:text-[var(--color-primary4)] text-sm font-bold
}
.mgc-Chevron {
    mask-image: url('/chevron-right.svg');
    background-color: var(--color-primary4);
    mask-size: 12px 12px;
    width: 12px;
    height: 12px;
}
.mgc-Chevron:hover {
    background-color: var(--color-primary4);
}
.star-icn{
    mask-image: url('/star.svg');
    background-color: var(--color-primary4);
    mask-size: 18px 18px;
    width: 18px;
    height: 18px;  
}
.slide-img{
    object-fit: fill;
    height: 100%;
    width: 100%;
}
.side-cont{
    position: relative;
    width: 100%;
    height: 250px;
    min-height: 250px;
    border: 1px solid var(--banner-border);
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 3px 10px #0006;
    margin-bottom: 30px;   
}
</style>
